import { type ReactNode } from 'react';

import { type RequestContext } from '~/types/RequestContext';

import { type BettingInsightsContent, type EsIndex } from '../types';

// Type for the context that will be passed as first parameter to render functions
export interface BettingInsightsTableCaptionContext {
  environment: RequestContext['environment'];
  featureFlags: RequestContext['featureFlags'];
  repubConfig: RequestContext['repubConfig'];
  user: RequestContext['user'];
}

export interface BettingInsightsTableCaptionConfig<
  TIndex extends EsIndex,
  TContent extends BettingInsightsContent<TIndex> = BettingInsightsContent<TIndex>,
> {
  render: (context: BettingInsightsTableCaptionContext, data: { content: TContent; resultIndex: number }) => ReactNode;
}

// Type alias for the return type of createBettingInsightsTableCaption
export type BettingInsightsTableCaption<
  TIndex extends EsIndex,
  TContent extends BettingInsightsContent<TIndex> = BettingInsightsContent<TIndex>,
> = Readonly<{
  render: (data: { content: TContent; resultIndex: number }) => ReactNode;
}>;

/**
 * Factory function to create BettingInsightsTableCaption configuration (functional approach)
 * @param requestContext - The request context
 * @param config - The table caption configuration
 * @returns Immutable table caption configuration object
 */
export function createBettingInsightsTableCaption<
  TIndex extends EsIndex,
  TContent extends BettingInsightsContent<TIndex> = BettingInsightsContent<TIndex>,
>(
  requestContext: RequestContext,
  config: BettingInsightsTableCaptionConfig<TIndex, TContent>,
): BettingInsightsTableCaption<TIndex, TContent> {
  // Create context object
  const context: BettingInsightsTableCaptionContext = {
    environment: requestContext.environment,
    featureFlags: requestContext.featureFlags,
    repubConfig: requestContext.repubConfig,
    user: requestContext.user,
  };

  // Create the result object with wrapper function that injects context
  const result = {
    // Wrapper function that injects context as first parameter
    render: (data: { content: TContent; resultIndex: number }) => config.render(context, data),
  };

  return Object.freeze(result);
}
